import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, RotateCcw, ShieldOff, ExternalLink, ScrollText } from "lucide-react";
import AcceptanceLog from "@/components/AcceptanceLog";

const STATUS_COPY = {
  review_required: { label: "Review required", tone: "bg-[#D9583A]/10 text-[#D9583A] border-[#D9583A]/30" },
  partially_refunded: { label: "Partially refunded", tone: "bg-[#EFEADF] text-[#1A1A1A] border-[#E8E2D9]" },
  refunded: { label: "Fully refunded", tone: "bg-[#F5F2EB] text-[#5C5C5C] border-[#E8E2D9]" },
  no_refund_due: { label: "No refund due", tone: "bg-[#F5F2EB] text-[#5C5C5C] border-[#E8E2D9]" },
};

// Server-authoritative numbers only — nothing here derives eligibility or totals.
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const newRequestId = () => `rr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

function formatUSD(n) {
  return `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return String(iso); }
}

export default function RefundReviews({ scope = "vendor", onCountChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);   // enriched review being acted on
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [decision, setDecision] = useState("refund");
  const [submitting, setSubmitting] = useState(false);
  // One request_id per deliberate attempt: created when the dialog opens, reused across
  // retries of that same attempt, discarded only after success or when the dialog closes.
  const [requestId, setRequestId] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/refund-reviews")
      .then(({ data }) => { setRows(data); onCountChange?.(data.length); })
      .catch((e) => { setRows([]); onCountChange?.(0); if (e.response?.status !== 403) toast.error("Couldn't load refund reviews"); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAction = (row, initialDecision = "refund") => {
    if (initialDecision === "no_refund_due" && num(row.remaining_eligible) > 0) {
      return toast.error(`Policy still entitles the customer to ${formatUSD(row.remaining_eligible)}. Issue that refund first.`);
    }
    setActive(row);
    setDecision(initialDecision);
    // Default = eligible amount remaining (policy floor); fall back to full remaining when policy owes nothing.
    const eligibleRemaining = num(row.remaining_eligible);
    setAmount(initialDecision === "refund" ? String(eligibleRemaining > 0 ? eligibleRemaining : num(row.remaining_refundable)) : "");
    setReason("");
    setRequestId(newRequestId());
  };
  const close = () => { setActive(null); setAmount(""); setReason(""); setRequestId(""); };

  const submit = async () => {
    if (!active) return;
    if (submitting) return;
    const maxRefund = num(active.remaining_refundable);
    const minRefund = num(active.remaining_eligible);
    if (decision === "refund") {
      const amt = Number(amount);
      if (!Number.isFinite(amt) || amt <= 0) return toast.error("Enter a refund amount greater than $0");
      if (amt > maxRefund + 0.001) return toast.error(`Refund cannot exceed remaining collected ${formatUSD(maxRefund)}`);
      if (minRefund > 0 && amt + 0.001 < minRefund) return toast.error(`Policy requires at least ${formatUSD(minRefund)} — the customer's eligible refund remaining`);
    } else if (minRefund > 0) {
      return toast.error(`Policy still entitles the customer to ${formatUSD(minRefund)}. "No refund due" is unavailable.`);
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/bookings/${active.booking_id}/refund`, {
        booking_id: active.booking_id,
        decision,
        amount: decision === "refund" ? Number(amount) : null,
        reason,
        request_id: requestId,
      });
      if (data?.already_processed) toast.info("This refund was already processed — showing the recorded result.");
      else toast.success(decision === "refund" ? (data?.refund_status === "refunded" ? "Full refund issued" : "Partial refund issued") : "Refund review resolved — no refund due");
      close();          // success → request_id discarded; a new attempt gets a new id
      load();           // totals/status come back from the server
    } catch (e) {
      // Failure: dialog stays open and request_id is kept so a retry replays the same attempt.
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-[#5C5C5C]" data-testid="refund-reviews-loading">Loading refund reviews…</div>;

  return (
    <div data-testid={`refund-reviews-${scope}`}>
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <div className="eyebrow">Refund review</div>
          <div className="text-sm text-[color:var(--text-muted)] mt-1">
            Cancelled bookings with money on the line — decide the refund based on the terms in place when the customer accepted.
          </div>
        </div>
        <div className="text-sm text-[color:var(--text-muted)]" data-testid="refund-reviews-count">{rows.length} pending</div>
      </div>

      {rows.length === 0 ? (
        <div className="card-elegant p-8 text-center text-[#5C5C5C]" data-testid="refund-reviews-empty">
          No refund reviews right now.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const badge = STATUS_COPY[r.refund_status] || { label: r.refund_status, tone: "bg-[#F5F2EB] text-[#5C5C5C] border-[#E8E2D9]" };
            const snap = r.payment_policy_snapshot;
            const computed = snap?.computed || null;
            return (
              <div
                key={r.booking_id}
                data-testid={`refund-review-${r.booking_id}`}
                className="card-elegant p-5 sm:p-6"
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${badge.tone}`}>
                        {badge.label}
                      </span>
                      <Link to={`/bookings/${r.booking_id}`} className="text-sm link-underline inline-flex items-center gap-1">
                        View booking <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                    <div className="font-serif text-xl sm:text-2xl mt-2 truncate">
                      {r.planner_name} × {r.vendor_name}
                    </div>
                    <div className="text-xs sm:text-sm text-[color:var(--text-muted)] mt-1">
                      Event {formatDate(r.event_date)} · Cancelled {formatDate(r.refund_review_created_at)}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-5 text-right sm:min-w-[520px]">
                    <Cell label="Collected" value={formatUSD(r.amount_collected)} testId={`amt-paid-${r.booking_id}`} />
                    <Cell label="Refunded" value={formatUSD(r.amount_refunded)} testId={`amt-refunded-${r.booking_id}`} />
                    <Cell label="Eligible" value={formatUSD(r.eligible_amount)} testId={`amt-eligible-${r.booking_id}`} />
                    <Cell label="Eligible left" value={formatUSD(r.remaining_eligible)} tone={num(r.remaining_eligible) > 0 ? "text-[#D9583A]" : ""} testId={`amt-eligible-remaining-${r.booking_id}`} />
                    <Cell label="Refundable" value={formatUSD(r.remaining_refundable)} testId={`amt-remaining-${r.booking_id}`} />
                  </div>
                </div>

                {/* Server-side policy calculation frozen at cancellation */}
                {r.policy_calc && (
                  <div className="mt-4 text-xs sm:text-sm text-[color:var(--text-muted)] leading-relaxed rounded-md bg-[#F5F2EB] p-3" data-testid={`policy-basis-${r.booking_id}`}>
                    <strong className="text-[color:var(--text)]">Policy basis:</strong>{" "}
                    {r.policy_calc.within_free_window ? "Inside the free-cancellation window" : "Outside the free-cancellation window"}
                    {" · "}{num(r.policy_calc.refund_percent)}% of {formatUSD(r.policy_calc.collected)} collected
                    {r.policy_calc.hours_before_event != null && ` · cancelled ${Math.max(0, Math.round(num(r.policy_calc.hours_before_event) / 24))} day(s) before the event`}
                    {` · free until ${Math.round(num(r.policy_calc.free_until_hours) / 24)} days out, then ${num(r.policy_calc.refund_percent_after)}%`}
                    {r.policy_calc.policy_snapshot_available === false && " · platform default policy"}
                  </div>
                )}

                {/* Snapshotted policy — the terms in effect when the customer accepted */}
                <div className="mt-4 pt-4 border-t border-[color:var(--border)]">
                  <div className="eyebrow mb-2">Terms customer accepted</div>
                  {snap ? (
                    <div className="text-xs sm:text-sm text-[color:var(--text-muted)] leading-relaxed space-y-1">
                      {computed?.deposit_summary && <div><strong className="text-[color:var(--text)]">Deposit:</strong> {computed.deposit_summary}</div>}
                      {computed?.balance_summary && <div><strong className="text-[color:var(--text)]">Balance:</strong> {computed.balance_summary}</div>}
                      {computed?.cancellation_summary && <div><strong className="text-[color:var(--text)]">Cancellation:</strong> {computed.cancellation_summary}</div>}
                      {snap.rescheduling_policy && <div><strong className="text-[color:var(--text)]">Rescheduling:</strong> {snap.rescheduling_policy}</div>}
                      {snap.weather_policy && <div><strong className="text-[color:var(--text)]">Weather:</strong> {snap.weather_policy}</div>}
                      {snap.other_terms && <div><strong className="text-[color:var(--text)]">Other:</strong> {snap.other_terms}</div>}
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-[#F5F2EB] text-[#5C5C5C]" data-testid={`no-snapshot-${r.booking_id}`}>
                      <AlertTriangle className="w-3.5 h-3.5" /> Policy snapshot unavailable — decide based on written communication and current platform default.
                    </div>
                  )}
                </div>

                {r.actions?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[color:var(--border)]">
                    <div className="eyebrow mb-2">History</div>
                    <ul className="space-y-1 text-xs text-[color:var(--text-muted)]" data-testid={`refund-history-${r.booking_id}`}>
                      {r.actions.map((a) => (
                        <li key={a.action_id} className="flex items-center gap-3">
                          <span className="text-[color:var(--text)] font-medium">{a.decision === "refund" ? `Refund ${formatUSD(a.amount)}` : "No refund due"}</span>
                          <span>·</span>
                          <span>{a.user_role || "user"}</span>
                          <span>·</span>
                          <span>{formatDate(a.created_at)}</span>
                          {a.note && <span className="italic truncate">"{a.note}"</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <details className="mt-4 pt-4 border-t border-[color:var(--border)]">
                  <summary className="cursor-pointer text-xs inline-flex items-center gap-2 text-[color:var(--accent)] link-underline" data-testid={`toggle-acceptance-log-${r.booking_id}`}>
                    <ScrollText className="w-3.5 h-3.5" /> View legal acceptance log
                  </summary>
                  <div className="mt-3">
                    <AcceptanceLog bookingId={r.booking_id} compact />
                  </div>
                </details>

                <div className="mt-4 pt-4 border-t border-[color:var(--border)] flex flex-col sm:flex-row gap-2">
                  <Button
                    data-testid={`open-refund-${r.booking_id}`}
                    onClick={() => openAction(r, "refund")}
                    disabled={r.remaining_refundable <= 0}
                    className="btn-sage rounded-full h-10 disabled:opacity-50"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" /> Issue refund
                  </Button>
                  <Button
                    data-testid={`open-no-refund-${r.booking_id}`}
                    onClick={() => openAction(r, "no_refund_due")}
                    variant="outline"
                    disabled={num(r.remaining_eligible) > 0}
                    title={num(r.remaining_eligible) > 0 ? `Unavailable — policy entitles the customer to ${formatUSD(r.remaining_eligible)}` : undefined}
                    className="rounded-full h-10 border-[color:var(--border-strong)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ShieldOff className="w-4 h-4 mr-2" /> No refund due
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent data-testid="refund-action-dialog" className="max-w-md bg-[#FDFBF7]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {decision === "refund" ? "Issue a refund" : "Mark: No refund due"}
            </DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-4 text-sm">
              <div className="rounded-md bg-[#F5F2EB] p-4 space-y-2">
                <Row label="Original amount paid" v={formatUSD(active.amount_collected)} />
                <Row label="Previously refunded" v={formatUSD(active.amount_refunded)} />
                <Row label="Policy-eligible refund" v={formatUSD(active.eligible_amount)} />
                <Row label="Eligible remaining (minimum)" v={formatUSD(active.remaining_eligible)} tone={num(active.remaining_eligible) > 0 ? "text-[#D9583A]" : ""} strong />
                <Row label="Maximum refundable now" v={formatUSD(active.remaining_refundable)} strong />
                {decision === "refund" && (
                  <Row label="Refund being issued" v={formatUSD(Number(amount) || 0)} tone="text-[#D9583A]" strong />
                )}
              </div>

              {decision === "refund" && (
                <div>
                  <label className="eyebrow block mb-2" htmlFor="refund-amount">Refund amount ($)</label>
                  <Input
                    id="refund-amount"
                    data-testid="refund-amount-input"
                    type="number"
                    step="0.01"
                    min={num(active.remaining_eligible) > 0 ? active.remaining_eligible : "0.01"}
                    max={active.remaining_refundable}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="bg-white h-11"
                  />
                  <div className="flex gap-2 mt-2">
                    {num(active.remaining_eligible) > 0 && (
                      <button type="button" data-testid="refund-preset-eligible" onClick={() => setAmount(String(active.remaining_eligible))} className="text-xs rounded-full px-3 py-1 border border-[color:var(--border)] hover:border-[color:var(--border-strong)]">Eligible {formatUSD(active.remaining_eligible)}</button>
                    )}
                    <button type="button" data-testid="refund-preset-full" onClick={() => setAmount(String(active.remaining_refundable))} className="text-xs rounded-full px-3 py-1 border border-[color:var(--border)] hover:border-[color:var(--border-strong)]">Full remaining {formatUSD(active.remaining_refundable)}</button>
                  </div>
                </div>
              )}

              <div>
                <label className="eyebrow block mb-2" htmlFor="refund-reason">
                  {decision === "refund" ? "Reason (optional)" : "Rationale (recommended)"}
                </label>
                <Textarea
                  id="refund-reason"
                  data-testid="refund-reason-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={decision === "refund"
                    ? "e.g. Cancelled 60+ days out — full deposit refund per policy"
                    : "e.g. Cancelled 5 days out — deposit non-refundable per accepted policy"}
                  className="bg-white"
                  rows={3}
                />
              </div>

              <p className="text-[11px] text-[color:var(--text-muted)] leading-relaxed">
                {decision === "refund"
                  ? "This reverses the Stripe charge for the amount above. The server enforces the policy minimum and the collected maximum; nothing is marked refunded until Stripe confirms."
                  : "This resolves the review without issuing a Stripe transaction. It cannot be undone."}
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={close} disabled={submitting} className="rounded-full">Cancel</Button>
            <Button
              data-testid="confirm-refund-action-btn"
              onClick={submit}
              disabled={submitting}
              className={`rounded-full text-white ${decision === "refund" ? "bg-[#D9583A] hover:bg-[#C04A2E]" : "bg-[#1A1A1A] hover:bg-black"}`}
            >
              {submitting ? "Processing…" : decision === "refund" ? "Confirm refund" : "Confirm no refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Cell({ label, value, tone = "", testId }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className={`font-serif text-lg sm:text-xl mt-1 ${tone}`} data-testid={testId}>{value}</div>
    </div>
  );
}
function Row({ label, v, tone = "", strong }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-[color:var(--text-muted)]">{label}</span>
      <span className={`${strong ? "font-semibold" : ""} ${tone}`}>{v}</span>
    </div>
  );
}
